import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import type { CategorySlice, Dashboard as DashboardData } from "@finances/shared";
import styles from "./Dashboard.module.css";
import { focusLink, focusParam, focusSummary, parseFocus } from "./focus.js";
import { SliceTransactions } from "./SliceTransactions.js";
import { RequestFailed } from "../../lib/api.js";
import {
  formatDay,
  formatMonth,
  formatMonthShort,
  formatPeriod,
  pluralise,
} from "../../lib/format.js";
import {
  resolveScope,
  scopeLabel,
  setLastMonth,
  setLastPeriod,
  toTransactionsSearch,
  type Scope,
} from "../../lib/lastMonth.js";
import { useDashboard, useOnboarding } from "../../lib/queries.js";
import { useSession } from "../../lib/session.js";
import { Checklist } from "../onboarding/Checklist.js";
import { visible } from "../onboarding/steps.js";
import {
  Alert,
  BarChart,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Heading,
  LineChart,
  LineKey,
  Money,
  MultiSelect,
  PeriodPicker,
  Row,
  Skeleton,
  Stack,
  Stat,
  Tabs,
  Text,
  type LinePoint,
  type LineSeries,
  type TabOption,
} from "../../ui/index.js";

/**
 * The month — or the range — at a glance, with every figure one tap from the
 * transactions behind it. Trust in a finance app is built by making it
 * unnecessary.
 */
export function Dashboard() {
  const { householdId } = useSession();
  const search = useSearch({ from: "/" });
  const navigate = useNavigate();

  const scope = resolveScope(search);
  const accountIds = parseFocus(search.accountIds);
  const query = useDashboard(householdId, scope, accountIds);
  const label = query.data ? formatPeriod(query.data.period) : scopeLabel(scope, formatMonth);

  return (
    <Stack gap={5}>
      <Row justify="between" align="center" wrap gap={3}>
        <Heading level={1} size="2xl">
          {label}
        </Heading>
        <div className={styles.pickers}>
          <div className={styles.picker}>
            <PeriodPicker
              months={query.data?.months ?? (scope.month ? [scope.month] : [])}
              value={scope}
              format={formatMonth}
              onChange={(next) => {
                if ("month" in next) setLastMonth(next.month);
                else setLastPeriod(next.period);
                // The focus travels with the period: changing the month is not
                // a request to start counting three more accounts.
                void navigate({ to: "/", search: { ...next, accountIds: search.accountIds } });
              }}
            />
          </div>
          <div className={styles.picker}>
            <MultiSelect
              label="Accounts"
              allLabel="All accounts"
              summary={focusSummary(accountIds, query.data?.accounts ?? [])}
              options={(query.data?.accounts ?? []).map((account) => ({
                value: account.id,
                label: account.label,
              }))}
              selected={accountIds}
              onChange={(next) =>
                void navigate({ to: "/", search: { ...scope, accountIds: focusParam(next) } })
              }
            />
          </div>
        </div>
      </Row>

      {/* Ahead of the figures, and not waiting on them: for the household this
          is addressed to there are no figures yet, and the thing worth showing
          first is what to do about that. */}
      <Checklist />

      {query.isPending && <Loading />}

      {query.error && (
        <ErrorState
          title="Could not load this"
          body={
            query.error instanceof RequestFailed
              ? query.error.message
              : "Something went wrong loading the dashboard."
          }
          onRetry={() => void query.refetch()}
        />
      )}

      {query.data && (
        <Loaded data={query.data} scope={scope} accountIds={accountIds} label={label} />
      )}
    </Stack>
  );
}

function Loading() {
  return (
    <Stack gap={5} aria-busy="true">
      <Card title="This period">
        <Stack gap={4}>
          <Skeleton height="var(--space-10)" />
          <div className={styles.figures}>
            {[0, 1, 2].map((index) => (
              <Stack gap={2} key={index}>
                <Skeleton text width="60%" />
                <Skeleton text width="80%" />
              </Stack>
            ))}
          </div>
        </Stack>
      </Card>
      <Card title="Where it went">
        <Stack gap={3}>
          {[0, 1, 2, 3, 4].map((index) => (
            <Skeleton key={index} height="var(--space-5)" />
          ))}
        </Stack>
      </Card>
    </Stack>
  );
}

function Loaded({
  data,
  scope,
  accountIds,
  label,
}: {
  data: DashboardData;
  scope: Scope;
  accountIds: string[];
  label: string;
}) {
  const { householdId } = useSession();
  const onboarding = useOnboarding(householdId);
  const [view, setView] = useState<FlowView>("burn");
  /* The checklist is already asking for an account, in more detail and with
     the rest of the setup around it, so saying it twice on one screen is one
     screen shouting. Anyone who has put the checklist away still gets this. */
  const guiding = onboarding.data ? visible(onboarding.data) : false;

  const nothingAtAll = data.accounts.length === 0;
  const nothingInView = data.totals.count === 0;
  const txnSearch = { ...toTransactionsSearch(scope), ...focusLink(accountIds) };

  if (nothingAtAll) {
    if (guiding) return null;

    return (
      <Card>
        <EmptyState
          title="No accounts yet"
          body="Add the accounts you want to track, then import a statement for one of them."
          action={
            <Link to="/accounts">
              <Button variant="primary">Add an account</Button>
            </Link>
          }
        />
      </Card>
    );
  }

  return (
    <Stack gap={5}>
      {/**
       * The period as it happened rather than as one sum at the end of it.
       *
       * Two totals cannot say whether a month was steady or was one weekend,
       * and they cannot say that three weeks' allowance has gone by the 9th —
       * which is the question anyone opening this on the 9th is asking. The
       * figures underneath still say what it came to, with the movement on the
       * period before, so nothing the bars said has been lost.
       */}
      <Card title="This period">
        {/**
         * On a wide screen the figures move to the chart's side rather than
         * under it. Stacked, the card was a band the height of the screen and
         * the chart — which keeps its shape at any width — took most of it.
         */}
        <div className={styles.period}>
          {/**
           * Two readings of one period, and room for the next one. They answer
           * different questions — "are we inside the budget" and "what moved"
           * — and a card that drew them at once would be two charts nobody
           * chose.
           */}
          <Tabs label="Chart" tabs={VIEWS} value={view} onChange={setView}>
            {view === "burn" && <BurnChart data={data} />}
            {view === "flow" && <FlowChart data={data} />}
          </Tabs>
          <Stack gap={4}>
            <div className={styles.figures}>
              {flowStats(data, view).map((stat) => (
                <Stat key={stat.id} label={stat.label} value={stat.value} meta={stat.meta} />
              ))}
            </div>
            {/**
             * Each month above stands on its own, deliberately. This one does
             * not — it is every month's "left over" added together, so a July
             * that overspent while June sat on a surplus reads as still up,
             * not as a fresh shortfall. Hidden for all-time, where it would
             * just repeat "Left over": there is nothing before all-time to
             * carry in. Kept out of the chart's own zero-line scale, and set
             * apart by a rule, because it is a running position rather than
             * this period's own flow.
             */}
            {data.period.to !== null && (
              <div className={styles.runningTotal}>
                <Stat
                  label="Running total"
                  value={
                    <Money
                      pence={data.running.runningPence}
                      tone="balance"
                      size="2xl"
                      weight="semibold"
                    />
                  }
                  meta={carriedText(data)}
                />
              </div>
            )}
          </Stack>
        </div>
      </Card>

      <Coverage data={data} />

      {data.uncategorisedCount > 0 && (
        <Alert tone="warning">
          <Row justify="between" align="center" gap={3} wrap>
            <span>{pluralise(data.uncategorisedCount, "transaction")} still need a category.</span>
            <Link to="/transactions" search={{ ...txnSearch, uncategorised: true }}>
              <Button size="sm">Review them</Button>
            </Link>
          </Row>
        </Alert>
      )}

      {/**
       * The figures above are out by this much, and by how much is the point.
       *
       * Every total here drops rows filed under a transfer category. That is
       * right when both halves are in the ledger and wrong when only one is —
       * money from an account nobody imports is real income, and a payment to a
       * card nobody imports is real spending. So the gap is named with its
       * amount rather than left as a quiet subtraction.
       */}
      {data.unclassified.count > 0 && (
        <Alert tone="warning">
          <Row justify="between" align="center" gap={3} wrap>
            <span>
              {pluralise(data.unclassified.count, "transfer")} in this view{" "}
              {data.unclassified.count === 1 ? "has" : "have"} no matching payment elsewhere in your
              ledger, so {data.unclassified.count === 1 ? "it is" : "they are"} in none of the
              figures above. That is{" "}
              <Money pence={Math.abs(data.unclassified.pence)} tone="none" align={false} /> either
              way.
            </span>
            <Link to="/transactions" search={{ ...txnSearch, unclassifiedTransfer: true }}>
              <Button size="sm">Sort them out</Button>
            </Link>
          </Row>
        </Alert>
      )}

      {nothingInView ? (
        <Card>
          <EmptyState
            title={`Nothing imported for ${label}`}
            body="Upload a statement and it will show up here."
            action={
              <Link to="/import">
                <Button variant="primary">Import a statement</Button>
              </Link>
            }
          />
        </Card>
      ) : (
        <div className={styles.columns}>
          <Card
            title="Where it went"
            action={
              <Link to="/transactions" search={txnSearch} className={styles.quiet}>
                All transactions
              </Link>
            }
          >
            <Stack gap={3}>
              <BarChart
                bars={data.byCategory.map((slice) => ({
                  id: barId(slice),
                  label: slice.name,
                  pence: slice.spentPence,
                  colour: slice.colour,
                }))}
                emptyLabel="No spending recorded in this view."
                renderDetails={(bar) => {
                  const slice = data.byCategory.find((candidate) => barId(candidate) === bar.id);
                  return slice ? (
                    <SliceTransactions slice={slice} scope={scope} accountIds={accountIds} />
                  ) : null;
                }}
              />
              {/* Said here rather than implied by a bar that is not there. The
                  wording no longer claims they were all internal: that is only
                  true of the ones whose other half is in the ledger, and
                  `unclassified` below is the honest count of the rest. */}
              {data.totals.transferCount > 0 && (
                <Text size="xs" tone="muted">
                  {pluralise(data.totals.transferCount, "transfer")} left out of these figures —
                  {" moving money between your own accounts is neither spending nor income."}
                </Text>
              )}
            </Stack>
          </Card>

          <Stack gap={5}>
            <Card
              title="Accounts"
              action={
                <Text size="xs" tone="muted">
                  Imported here
                </Text>
              }
            >
              <Stack gap={3}>
                <Stack gap={0} as="ul" className={styles.list}>
                  {data.accounts.map((account) => (
                    <li key={account.id} className={styles.listRow}>
                      <Link
                        to="/transactions"
                        search={{ ...txnSearch, accountId: account.id }}
                        className={styles.listLink}
                      >
                        <span className={styles.listLabel}>{account.label}</span>
                        {/* Nothing imported is not the same fact as nothing
                            happened, so the empty row says which one it is. */}
                        <Text
                          as="span"
                          size="sm"
                          tone={account.txnCount === 0 ? "muted" : "primary"}
                          className={styles.listValue}
                        >
                          {account.txnCount === 0
                            ? "Nothing imported"
                            : pluralise(account.txnCount, "transaction")}
                        </Text>
                      </Link>
                    </li>
                  ))}
                </Stack>
                <Link to="/accounts">
                  <Button variant="secondary" size="sm">
                    Add an account
                  </Button>
                </Link>
              </Stack>
            </Card>

            <Card
              title="Recent"
              action={
                <Link to="/transactions" search={txnSearch} className={styles.quiet}>
                  See all
                </Link>
              }
            >
              <Stack gap={0} as="ul" className={styles.list}>
                {data.recent.map((txn) => (
                  <li key={txn.id} className={styles.listRow}>
                    <span className={styles.listLink}>
                      <span className={styles.listLabel}>
                        {txn.description}
                        <span className={styles.meta}>
                          {formatDay(txn.date)} · {txn.category}
                        </span>
                      </span>
                      <Money pence={txn.amountPence} className={styles.listValue} />
                    </span>
                  </li>
                ))}
              </Stack>
            </Card>
          </Stack>
        </div>
      )}
    </Stack>
  );
}

/**
 * The partial state, said out loud.
 *
 * An account with no rows in view is almost always a statement nobody has
 * uploaded yet, and a total that quietly leaves it out is worse than no total.
 */
/**
 * What these totals do not cover.
 *
 * Two different gaps, and the second was invisible for a long time. An account
 * with no rows at all is obvious once named. An account with *some* rows whose
 * statement stops three weeks short reads as complete, and every figure on the
 * screen is right about the rows it has and wrong about the month.
 */
function Coverage({ data }: { data: DashboardData }) {
  const { missing, behind, accountsWithData, accountsTotal } = data.coverage;
  if (accountsWithData === 0) return null;
  if (missing.length === 0 && behind.length === 0) return null;

  return (
    <Alert tone="warning">
      <Row justify="between" align="center" gap={3} wrap>
        <Stack gap={1}>
          {missing.length > 0 && (
            <span>
              These totals cover {String(accountsWithData)} of {String(accountsTotal)} accounts.
              Nothing imported yet for {missing.map((account) => account.label).join(", ")}.
            </span>
          )}
          {/**
           * A credit card is worth its own sentence. An unimported savings
           * account costs the figures a transfer that was not counted anyway; an
           * unimported card costs them every purchase made on it, while the
           * payments that cleared it sit in the ledger looking like transfers to
           * nowhere. Both gaps are "no statement", and only one of them quietly
           * removes a month of spending.
           */}
          {missing
            .filter((account) => account.kind === "credit_card")
            .map((account) => (
              <span key={account.id}>
                Nothing has been imported for {account.label}, so anything spent on it is missing
                from these figures — and the payments that cleared it are counted as transfers.
              </span>
            ))}
          {behind.map((account) => (
            <span key={account.id}>
              {account.label} only goes up to{" "}
              {account.lastTransactionDate
                ? formatDay(account.lastTransactionDate)
                : "an earlier date"}
              , so anything after that is missing from these figures.
            </span>
          ))}
        </Stack>
        <Link to="/import">
          <Button size="sm">Import</Button>
        </Link>
      </Row>
    </Alert>
  );
}

/**
 * A slice's identity in the chart. The unfiled rows have no category id, so
 * the name stands in for one — it is the same thing the server grouped by.
 */
function barId(slice: CategorySlice): string {
  return slice.categoryId ?? slice.name;
}

/**
 * Which slice of the period's spending one category took.
 *
 * The share matters more than the amount it is drawn from: £480 on groceries
 * is a different fact in a month that spent £600 and in one that spent £6,000,
 * and the card has room to say which.
 */
type Side = "in" | "out" | "saving";

/**
 * Shown when this view set something aside, or when the period before did and
 * this one did not — a household that has stopped saving needs to see the
 * zero, whereas one that has never had a saving category does not need the
 * card.
 */
function savingShown(data: DashboardData): boolean {
  return data.totals.savingPence !== 0 || (data.previous?.savingPence ?? 0) !== 0;
}

/**
 * The figure a card compares, the right way up: positive is more of the thing
 * the card is named after.
 *
 * Negated rather than `Math.abs` for out and saving, which is the difference
 * between "£2,000 set aside" and "£2,000 taken out". Both have the same
 * magnitude and they are opposite facts, and abs reported them as the same one.
 */
function sideOf(totals: DashboardData["totals"], side: Side): number {
  if (side === "in") return totals.inPence;
  return -(side === "out" ? totals.outPence : totals.savingPence);
}

/** A month bucket's date on the axis: `2026-07-01` → `Jul 26`. */
function formatFlowMonth(at: string): string {
  return formatMonthShort(at.slice(0, 7));
}

/**
 * Two climbing lines: everything that has come in, and everything that has
 * gone out, as the period went along.
 *
 * Cumulative, which is the whole point — a day's own spending is noise, and
 * the gap between the two lines on any given day is what the period was worth
 * up to it. Both are drawn as magnitudes so that both climb: money out is
 * negative in every figure in the app and it is not negative on an axis
 * called "spent so far".
 */
function flowSeries(data: DashboardData): LineSeries[] {
  const moneyIn: LinePoint[] = [];
  const moneyOut: LinePoint[] = [];
  let runningIn = 0;
  let runningOut = 0;

  for (const point of data.flow.points) {
    runningIn += point.inPence;
    runningOut -= point.outPence;
    moneyIn.push({ at: point.at, pence: runningIn });
    moneyOut.push({ at: point.at, pence: runningOut });
  }

  return [
    { id: "in", label: "Money in", points: moneyIn, tone: "in" },
    { id: "out", label: "Money out", points: moneyOut, tone: "out" },
  ];
}

/**
 * Spending against the budget, and nothing else on the chart.
 *
 * A flat line at the allowance and a climbing line of what has been spent
 * against it: the two only meet when the budget is gone, and where they meet
 * is the day it happened. Income is not here on purpose — this view answers
 * "are we inside the budget", which is a question about spending alone, and
 * the other two tabs are where the wages are.
 *
 * The spend line is green up to the crossing and red past it, because being
 * over is the one state on this chart worth colouring. It counts what the
 * budgets screen counts — booked spending in categories with an allowance —
 * so it can be read against the line above it without the two meaning
 * different things by "spending".
 */
function burnSeries(data: DashboardData): LineSeries[] {
  const { flow } = data;
  if (flow.budgetPence === null || !flow.from || !flow.to) return [];

  let spent = 0;
  const points: LinePoint[] = [];

  for (const point of flow.points) {
    spent -= point.budgetedPence;
    points.push({ at: point.at, pence: spent });
  }

  return [
    {
      id: "budget",
      label: "Budget",
      tone: "muted",
      reference: true,
      points: [
        { at: flow.from, pence: flow.budgetPence },
        { at: flow.to, pence: flow.budgetPence },
      ],
    },
    {
      id: "spent",
      label: "Spent against it",
      points,
      tone: "on-track",
      alertAbove: flow.budgetPence,
    },
  ];
}

/**
 * What is left of the allowance itself, negative once it has been overspent —
 * the budgets screen's figure, counting only what came off a budget.
 *
 * It counts only what came off a budget, so it is not the period's whole
 * position: wages and unbudgeted spending do not move it.
 */
function budgetLeft(data: DashboardData): number | null {
  if (data.flow.budgetPence === null) return null;
  return data.flow.points.reduce(
    (left, point) => left + point.budgetedPence,
    data.flow.budgetPence,
  );
}

type FlowView = "burn" | "flow";

/**
 * First is the default, and the default is the budget: "are we inside it" is
 * the question the dashboard is opened to answer, and the other is the one
 * asked afterwards.
 */
const VIEWS: Array<TabOption<FlowView>> = [
  { value: "burn", label: "Budget" },
  { value: "flow", label: "In and out" },
];

/** The axis both charts share: the whole period, however far the data got. */
function axis(data: DashboardData) {
  return {
    /* Both ends are null only when there is nothing at all to draw, and then
       the chart shows its empty label rather than an axis. */
    from: data.flow.from ?? "",
    to: data.flow.to ?? "",
    formatAt: data.flow.granularity === "day" ? formatDay : formatFlowMonth,
    /* The figures below the card name the lines and carry their keys, so a
       legend here would be the same row said twice. */
    legend: false,
  };
}

function FlowChart({ data }: { data: DashboardData }) {
  return (
    <LineChart
      {...axis(data)}
      series={flowSeries(data)}
      emptyLabel="Nothing imported for this period yet."
    />
  );
}

/**
 * What the two budget charts say when there is no budget to draw against.
 *
 * All-time has none worth drawing rather than a budget of nothing — budgets
 * are set a month at a time. A household that has simply never set one has
 * somewhere to go: this is the only place on the dashboard that says budgets
 * exist at all.
 */
function NoBudget({ data, invitation }: { data: DashboardData; invitation: string }) {
  if (data.period.to === null) {
    return (
      <EmptyState
        title="Not for all time"
        body="Budgets are set a month at a time, so there is no one figure to draw across every month there has ever been. Pick a month or a range."
      />
    );
  }

  return (
    <EmptyState
      title="No budgets set yet"
      body={invitation}
      action={
        <Link to="/budgets">
          <Button variant="primary">Set a budget</Button>
        </Link>
      }
    />
  );
}

function BurnChart({ data }: { data: DashboardData }) {
  if (data.flow.budgetPence === null) {
    return (
      <NoBudget
        data={data}
        invitation="Set what each category is allowed and this draws the month's spending against it, day by day."
      />
    );
  }

  return (
    <LineChart
      {...axis(data)}
      /* Its own legend, unlike the other tab: neither of these lines is a
         figure in the row underneath, so nothing down there can name them. */
      legend
      series={burnSeries(data)}
      emptyLabel="Nothing imported for this period yet."
    />
  );
}

interface FlowStat {
  id: string;
  label: ReactNode;
  value: ReactNode;
  meta?: ReactNode;
}

/**
 * A figure's label, wearing the key of the line it belongs to — but only while
 * that line is the one on screen. This row is the legend of whichever chart the
 * tabs are showing as well as the period's figures, which is why the chart
 * draws no legend of its own and why a key appears and disappears with it.
 */
function keyed(label: string, tone: "in" | "out", shown: boolean): ReactNode {
  if (!shown) return label;
  return (
    <span className={styles.statLabel}>
      <LineKey tone={tone} />
      {label}
    </span>
  );
}

/**
 * What the period came to, under the chart of how it got there.
 *
 * Every figure keeps its own sign here — money out is genuinely negative, not
 * `Math.abs`'d — because these are the ledger's figures rather than heights on
 * an axis, and this is where the comparison with the period before is said.
 */
function flowStats(data: DashboardData, view: FlowView): FlowStat[] {
  const stats: FlowStat[] = [
    {
      id: "in",
      label: keyed("Money in", "in", view === "flow"),
      value: <Money pence={data.totals.inPence} weight="semibold" size="lg" />,
      meta: movement(data, "in"),
    },
    {
      id: "out",
      label: keyed("Money out", "out", view === "flow"),
      value: <Money pence={data.totals.outPence} weight="semibold" size="lg" />,
      meta: movement(data, "out"),
    },
  ];

  const left = budgetLeft(data);
  if (data.flow.budgetPence !== null && left !== null) {
    stats.push({
      id: "budget",
      label: "Budget",
      value: <Money pence={data.flow.budgetPence} tone="none" weight="semibold" size="lg" />,
      /* What is left of the budget itself — the figure the budgets screen
         shows for the same month, to the penny. */
      meta: (
        <>
          <Money pence={Math.abs(left)} tone="none" align={false} />
          {left < 0 ? " over" : " left"}
        </>
      ),
    });
  }

  if (savingShown(data)) {
    stats.push({
      id: "saving",
      // The label follows the sign, because the magnitude alone is two
      // different facts. A month that drew £2,300 out of savings read
      // "Saved £2,300.00" for as long as this ignored it.
      label: data.totals.savingPence > 0 ? "Taken from savings" : "Saved",
      value: <Money pence={data.totals.savingPence} tone="none" weight="semibold" size="lg" />,
      meta: movement(data, "saving"),
    });
  }

  stats.push({
    id: "net",
    /**
     * "Under budget by" until this card had a budget on it. That phrase meant
     * money in less money out, which is not the budget three inches to its
     * left, and two things called the budget on one card is one of them being
     * read as the other.
     */
    label: data.totals.netPence < 0 ? "Short by" : "Left over",
    value: <Money pence={data.totals.netPence} tone="balance" weight="semibold" size="lg" />,
    meta: pluralise(data.totals.count, "transaction"),
  });

  return stats;
}

/**
 * What the running total started this period with, before its own money in
 * and out — the fact that turns a period's own "left over" into the
 * household's real position.
 */
function carriedText(data: DashboardData): ReactNode {
  const { carriedInPence } = data.running;
  if (carriedInPence === 0) return "Nothing carried in from earlier";
  return (
    <>
      <Money pence={Math.abs(carriedInPence)} tone="none" align={false} />{" "}
      {carriedInPence > 0 ? "carried in from earlier" : "shortfall carried in from earlier"}
    </>
  );
}

function movement(data: DashboardData, side: Side): string | undefined {
  if (!data.previous) return undefined;

  const now = sideOf(data.totals, side);
  const before = sideOf(data.previous, side);
  if (before === 0) return undefined;

  /**
   * A sign change has no useful percentage. A month that saved £2,000 against
   * one that withdrew £2,000 is not "level" and it is not "up 200%" — it is a
   * different thing happening, and saying so beats a number that has to be
   * decoded. This was reported as "Level with the period before".
   */
  if (Math.sign(now) !== Math.sign(before)) {
    if (side === "saving") {
      return now < 0
        ? "Took money out, having saved before"
        : "Saved, having taken money out before";
    }
    return "Not comparable with the period before";
  }

  const change = Math.round(((now - before) / before) * 100);
  if (change === 0) return "Level with the period before";
  return `${change > 0 ? "Up" : "Down"} ${String(Math.abs(change))}% on the period before`;
}
